"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getEBTSDateStringFromDate = exports.getISODateStringFromEBTSDateString = exports.generateEBTSFileBuffer = exports.overrideImageTypeRecordMetadata = exports.augmentJEP = exports.getJEP = exports.augmentJFDFields = exports.nextIDC = exports.getIDC = exports.getTOT = exports.marshallRecords = exports.marshallRecordLength = exports.calculateRecordLength = exports.marshallType4ImageRecord = exports.getFingerprintDetailsFromBioType = exports.marshallASCIIRecord = exports.marshallValue = exports.marshallSubfield = exports.marshallRepetition = exports.isRawData = exports.isRepetition = exports.isSubfield = exports.getTypeFromIndex = exports.getIndexParts = exports.getCNT = exports.getMappedRecord = exports.isValueEmpty = exports.skipGeneratedFields = exports.hasToSkipField = exports.sortJFDFieldsByType = exports.sanitizeIndex = void 0;
const jfd_1 = require("@le2/jfd");
const byte_tools_1 = require("./byte-tools");
const errors_1 = require("./errors");
const images_1 = require("./images");
const types_1 = require("./types");
/**
 * Sanitize an index to remove trailing whitespace and colons, also fix the padding for each type
 * @param index
 */
function sanitizeIndex(index) {
    const [fieldType, idx] = getIndexParts(index);
    // Remove leading 0s
    const unpadded = idx.replace(/^0*/, '');
    // Remove trailing :s
    const sanitized = unpadded.trim().replace(/:/g, '');
    // Trim down Type 1 indexes to 2 spaces
    if (fieldType === types_1.EbtsRecordType.Type1) {
        return `${fieldType}.${sanitized.padStart(2, '0')}`;
    }
    else {
        // Everything else can use 3 spaces
        return `${fieldType}.${sanitized.padStart(3, '0')}`;
    }
}
exports.sanitizeIndex = sanitizeIndex;
/**
 * Classify the fields in a JFD and return a record with the fields grouped under the field type key, returning a
 * ready-to-map list of (name, ebts parameters, index) tuples.
 * @param jfd - A valid JFD
 * @returns A dictionary with the fields classified by type
 */
function sortJFDFieldsByType(jfd) {
    const jfdDef = jfd.jfdDef;
    const separatedFields = new types_1.TypeRecord();
    // Iterate once over the list of fields and classify them accordingly
    Object.keys(jfdDef.fields).forEach((fieldName) => {
        const field = jfdDef.fields[fieldName];
        // Skip fields with empty EBTS
        if (!field.EBTS)
            return;
        const index = field.EBTS.index;
        const fieldType = getTypeFromIndex(index);
        const idx = sanitizeIndex(index);
        // Warn about unsupported field types
        const supported = Object.values(types_1.EbtsRecordType);
        if (!supported.includes(fieldType)) {
            console.warn(`Field type [${fieldType}] is unsupported. Supported field types are [${supported.join(', ')}]`);
        }
        // Create entry in record if it doesn't exist
        if (!(fieldType in separatedFields))
            separatedFields[fieldType] = [];
        separatedFields[fieldType].push([fieldName, idx]);
    });
    return separatedFields;
}
exports.sortJFDFieldsByType = sortJFDFieldsByType;
/**
 * Check the given mnemonic against the static list of rules of fields we want to skip
 * @param mnemonic - A mnemonic
 * @return Whether we want to skip it
 */
function hasToSkipField(mnemonic) {
    return types_1.JEP_SKIP_FIELDS_RULES.some((rule) => rule.test(mnemonic));
}
exports.hasToSkipField = hasToSkipField;
/**
 * Skip the fields that are supposed to be generated later and shouldn't really be in the JEP at the moment.
 * @param jep A jep to filter
 * @return The filtered JEP
 */
function skipGeneratedFields(jep) {
    return Object.keys(jep)
        .filter(types_1.not(hasToSkipField))
        .reduce((newJEP, key) => (Object.assign(Object.assign({}, newJEP), { [key]: jep[key] })), {});
}
exports.skipGeneratedFields = skipGeneratedFields;
/**
 * Recursively check if the provided value is empty
 * @param value - A JFDValue
 */
function isValueEmpty(value) {
    // Handle raw data
    if (isRawData(value)) {
        return value.length === 0;
    }
    // Handle MOS and MONS
    if (isRepetition(value)) {
        // An empty repetition equals an empty value
        if (value.length === 0)
            return true;
        // Check if at least one value in the repetition is empty
        // Use recursion, a subfield can't contain a repetition
        return value.every(isValueEmpty);
    }
    // Handle SOS and SONS
    if (isSubfield(value)) {
        const subfields = Object.values(value);
        // An empty subfield equals an empty value
        if (subfields.length === 0)
            return true;
        return subfields.every((v) => !v);
    }
    // Finally validate a simple SONS
    return !value;
}
exports.isValueEmpty = isValueEmpty;
/**
 * Map the (name, index) tuple to the data to return a (index, data) tuple
 * @param names - A 2-tuple containing the names and indexes from a JFD
 * @param jep - A valid JEP structure to map data from
 * @returns A list of mapped ASCII fields
 */
function getMappedRecord(names, jep) {
    return names
        .map(([name, index]) => {
        const value = jep[name];
        return [index, value];
    })
        .filter(([, value]) => !isValueEmpty(value));
}
exports.getMappedRecord = getMappedRecord;
/**
 * Construct a CNT based on the input IDCs
 * @param idcs - An array of the IDCs to generate the CNT from
 * @param last - The last number of the sequence of IDCs in the CNT
 * @return A JEP containing representing the CNT
 */
function getCNT(idcs, last) {
    // Generate the first element and append the (Field Type, IDC) pairs
    const augmentedIdcs = [[types_1.EbtsRecordType.Type1, getIDC(last)], ...idcs];
    // Map each IDC entry to a valid JEPValue
    const cntEntries = augmentedIdcs.map(([fieldType, idc]) => {
        return {
            // The keys in this dict mean nothing to the marshaller
            T1_CNT_TYPE: fieldType,
            T1_CNT_VAL: idc.ImageDesignationCharacter
        };
    });
    return { FileContent: cntEntries };
}
exports.getCNT = getCNT;
/**
 * Process an index and return the a field type and index num tuple
 * @param index
 */
function getIndexParts(index) {
    const [fieldType, idx] = index.split('.');
    if (fieldType === '' || idx === '')
        throw new errors_1.BadIndexError();
    return [fieldType, idx];
}
exports.getIndexParts = getIndexParts;
/**
 * Receive an index and preserve only the part before the `.' aka the field
 * type. Throws `BadIndexError` in case the index is malformed
 * @param index - A string representing an index as per the JFD
 * @return The type of said index
 */
function getTypeFromIndex(index) {
    const [fieldType] = getIndexParts(index);
    return fieldType;
}
exports.getTypeFromIndex = getTypeFromIndex;
/**
 * Checks whether a value from the JEP is a subfield
 * @param value - A value coming from the JEP
 */
function isSubfield(value) {
    return typeof value === 'object' && !isRepetition(value) && !isRawData(value);
}
exports.isSubfield = isSubfield;
/**
 * Checks whether a value from the JEP is a repetition
 * @param value - A value coming from the JEP
 */
function isRepetition(value) {
    return Array.isArray(value);
}
exports.isRepetition = isRepetition;
/**
 * Checks whether a value from the JEP is raw binary data
 * @param value - A value coming from the JEP
 */
function isRawData(value) {
    return value instanceof Uint8Array;
}
exports.isRawData = isRawData;
/**
 * Marshalls a repetition into an EBTS-valid format
 * @param field - A repetition field
 */
function marshallRepetition(field) {
    // Repeated fields usually have the same type, if the first one is an object
    // then we have subfield repetition...
    if (isSubfield(field[0])) {
        const subfields = field.map((f) => marshallSubfield(f));
        return byte_tools_1.join(subfields, types_1.RS);
    }
    // Otherwise it's just value repetition
    return byte_tools_1.join(field.map(byte_tools_1.encode), types_1.RS);
}
exports.marshallRepetition = marshallRepetition;
/**
 * Marshalls a subfield into an EBTS-valid format
 * @param subfield - A subfield
 */
function marshallSubfield(subfield) {
    return byte_tools_1.join(Object.values(subfield).map(byte_tools_1.encode), types_1.US);
}
exports.marshallSubfield = marshallSubfield;
/**
 * Determines whether the value is a repetition, subfield or a plain value and  marshalls it into an EBTS-valid format
 * accordingly
 * @param field - A value from the JEP
 * @returns A marshalled buffer with the value
 */
function marshallValue(field) {
    const [index, value] = field;
    let marshalledValue;
    if (isRepetition(value))
        marshalledValue = marshallRepetition(value);
    else if (isSubfield(value))
        marshalledValue = marshallSubfield(value);
    else if (isRawData(value))
        marshalledValue = value;
    else
        marshalledValue = byte_tools_1.encode(value); // SingleOccurrenceNoSubfieldsValue is just a string
    return byte_tools_1.concat(byte_tools_1.encode(`${index}:`), marshalledValue);
}
exports.marshallValue = marshallValue;
/**
 * Takes in an ASCII record and marshalls it in an EBTS-valid format
 * @param record
 * @returns A marshalled buffer with the entire ASCII record
 */
function marshallASCIIRecord(record) {
    return byte_tools_1.append(byte_tools_1.join(record.map(marshallValue), types_1.GS), types_1.FS);
}
exports.marshallASCIIRecord = marshallASCIIRecord;
/**
 * Derive the EBTS finger position and impression type from a given BioType. We only support BioTypes that we know are
 * allowed in EBTS files. Note that EBTS supports a whole bunch of fancy impression types but we only support the first
 * two since we always use livescan devices.
 * @param bioType
 * @throws FingerPositionNotFoundError
 */
function getFingerprintDetailsFromBioType(bioType) {
    switch (bioType) {
        case jfd_1.BioType.RightThumbRoll:
            return [types_1.EbtsFingerPositions.RightThumb, types_1.EbtsImpressionType.LiveScanRolled];
        case jfd_1.BioType.RightIndexRoll:
            return [types_1.EbtsFingerPositions.RightIndex, types_1.EbtsImpressionType.LiveScanRolled];
        case jfd_1.BioType.RightMiddleRoll:
            return [types_1.EbtsFingerPositions.RightMiddle, types_1.EbtsImpressionType.LiveScanRolled];
        case jfd_1.BioType.RightRingRoll:
            return [types_1.EbtsFingerPositions.RightRing, types_1.EbtsImpressionType.LiveScanRolled];
        case jfd_1.BioType.RightLittleRoll:
            return [types_1.EbtsFingerPositions.RightLittle, types_1.EbtsImpressionType.LiveScanRolled];
        case jfd_1.BioType.LeftThumbRoll:
            return [types_1.EbtsFingerPositions.LeftThumb, types_1.EbtsImpressionType.LiveScanRolled];
        case jfd_1.BioType.LeftIndexRoll:
            return [types_1.EbtsFingerPositions.LeftIndex, types_1.EbtsImpressionType.LiveScanRolled];
        case jfd_1.BioType.LeftMiddleRoll:
            return [types_1.EbtsFingerPositions.LeftMiddle, types_1.EbtsImpressionType.LiveScanRolled];
        case jfd_1.BioType.LeftRingRoll:
            return [types_1.EbtsFingerPositions.LeftRing, types_1.EbtsImpressionType.LiveScanRolled];
        case jfd_1.BioType.LeftLittleRoll:
            return [types_1.EbtsFingerPositions.LeftLittle, types_1.EbtsImpressionType.LiveScanRolled];
        case jfd_1.BioType.RightThumbFlat:
            return [types_1.EbtsFingerPositions.PlainRightThumb, types_1.EbtsImpressionType.LiveScanPlain];
        case jfd_1.BioType.LeftThumbFlat:
            return [types_1.EbtsFingerPositions.PlainLeftThumb, types_1.EbtsImpressionType.LiveScanPlain];
        case jfd_1.BioType.RightFourSlap:
            return [types_1.EbtsFingerPositions.RightFourSlap, types_1.EbtsImpressionType.LiveScanPlain];
        case jfd_1.BioType.LeftFourSlap:
            return [types_1.EbtsFingerPositions.LeftFourSlap, types_1.EbtsImpressionType.LiveScanPlain];
        case jfd_1.BioType.TwoThumbSlap:
            return [types_1.EbtsFingerPositions.TwoThumbSlap, types_1.EbtsImpressionType.LiveScanPlain];
        default:
            // Throw an error if an unsupported BioType was provided
            throw new errors_1.FingerPositionNotFoundError(bioType);
    }
}
exports.getFingerprintDetailsFromBioType = getFingerprintDetailsFromBioType;
/**
 * Takes in a Type-4 image record and marshalls it. Field length and offsets are defined in the EBTS spec as follows:
 *
 * - length: 4 byte field that stores the record length (18 header bytes + image size in bytes)
 * - idc: 1 byte for the IDC number
 * - impressionType: 1 byte to store the EbtsImpressionType value
 * - fingerPositions: 6 bytes to store the EbtsFingerPositions value, padded to the right with 0xff
 * - imageScanningResolution: 1 byte to store the resolution option: 0 = Minimum Scanning Resolution, 1 = Native Scanning Resolution
 * - horizontalLineLength: 2 bytes to store the image width
 * - verticalLineLength: 2 bytes - image height
 * - compressionAlgorithm: 1 byte to save the format, usually EbtsImageFormat.WSQ20
 * - data: The rest of the record is occupied by the raw binary data of the image
 *
 * @param image
 * @returns A marshalled buffer with the image record
 */
function marshallType4ImageRecord(image) {
    // At this point we expect the image to have a metadata field
    const meta = image.info.metadata;
    // Proceed to marshall
    return byte_tools_1.concat(byte_tools_1.intToByteArray(parseInt(meta.length), 4), byte_tools_1.intToByteArray(image.idc, 1), byte_tools_1.intToByteArray(parseInt(meta.impressionType), 1), 
    // Field 4.004 must be padded to the right with 0xff
    byte_tools_1.intToByteArray(parseInt(meta.fingerPositions), 6, 0xff, false), 
    // As in OpenEBTS: 500 and over is a 1, otherwise a 0
    byte_tools_1.intToByteArray(parseInt(meta.imageScanningResolution) >= 500 ? 1 : 0, 1), byte_tools_1.intToByteArray(parseInt(meta.horizontalLineLength), 2), byte_tools_1.intToByteArray(parseInt(meta.verticalLineLength), 2), byte_tools_1.intToByteArray(parseInt(meta.compressionAlgorithm), 1), image.data.data);
}
exports.marshallType4ImageRecord = marshallType4ImageRecord;
/**
 * Takes in an index prefix and a record and calculates the record length, taking into account the length of the actual
 * number that represents the length
 * @param indexLength - A JFD field index length
 * @param recordLength - The length of the rest of the field
 * @return The full length of the field
 */
function calculateRecordLength(indexLength, recordLength) {
    const indexRecordLength = indexLength + recordLength;
    // We encode the numeric representation just to be sure
    const recordNumberLength = byte_tools_1.encode(`${indexRecordLength}`).length;
    const nextPower = Math.pow(10, recordNumberLength);
    let totalLength = indexRecordLength + recordNumberLength;
    if (totalLength >= nextPower)
        totalLength += 1;
    return totalLength + 1;
}
exports.calculateRecordLength = calculateRecordLength;
/**
 * Takes in a record and its type and returns an EBTS-valid record length field
 * i.e. T*_LEN with the record length
 * @param type - The type of the record
 * @param record - The marshalled record data
 */
function marshallRecordLength(type, record) {
    const index = sanitizeIndex(`${type}.01`);
    const recordLength = calculateRecordLength(index.length, record.length);
    // Add 1 to make up for the trailing GS
    return byte_tools_1.append(byte_tools_1.encode(`${index}:${recordLength + 1}`), types_1.GS);
}
exports.marshallRecordLength = marshallRecordLength;
/**
 * Marshall the records separated by type in an EBTS-valid format
 * @param mappedTypeRecords - A record of mapped JEP structures by Field Type
 * @returns A marshalled buffer
 */
function marshallRecords(mappedTypeRecords) {
    const records = Object.entries(mappedTypeRecords)
        // Don't marshall empty records.
        .filter(([, typeRecord]) => {
        return typeRecord.length !== 0;
    })
        // Marshall each mapped record according to its type
        .map(([fieldType, mappedRecords]) => {
        switch (fieldType) {
            // Marshall normal records
            case types_1.EbtsRecordType.Type1:
            case types_1.EbtsRecordType.Type2:
            case types_1.EbtsRecordType.Type10:
            case types_1.EbtsRecordType.Type14:
            case types_1.EbtsRecordType.Type15:
            case types_1.EbtsRecordType.Type17:
                const marshalledAscii = mappedRecords.map((asciiRecord) => {
                    const marshalledAsciiRecord = marshallASCIIRecord(asciiRecord);
                    const asciiRecordLength = marshallRecordLength(fieldType, marshalledAsciiRecord);
                    // Prepend the LogicalRecordLength field to the record
                    return byte_tools_1.concat(asciiRecordLength, marshalledAsciiRecord);
                });
                return byte_tools_1.concat(...marshalledAscii);
            // Type-4s are binary records
            case types_1.EbtsRecordType.Type4:
                const marshalledImages = mappedRecords.map(marshallType4ImageRecord);
                return byte_tools_1.concat(...marshalledImages);
            // Shouldn't be possible to create a TypedRecord with an unsupported Record Type, but we still return something
            default:
                return new Uint8Array();
        }
    });
    return byte_tools_1.concat(...records);
}
exports.marshallRecords = marshallRecords;
/**
 * Generate a TOT JEP
 * @param tot
 * @returns A JEP with the TOT
 */
function getTOT(tot) {
    return { TypeOfTransaction: tot };
}
exports.getTOT = getTOT;
/**
 * Generate an IDC JEP
 * @param idc IDC number
 * @returns A JEP with the IDC
 */
function getIDC(idc) {
    return { ImageDesignationCharacter: `${idc}`.padStart(2, '0') };
}
exports.getIDC = getIDC;
/**
 * Get the next numeric IDC, the JEP and the new IDCs list
 * @param idcs
 * @param idcType
 * @param idc
 * @returns The next IDC number and the (number, JEP, IDCs) 3-tuple
 */
function nextIDC(idcs, idcType, idc) {
    const idcJep = getIDC(idc++); // This increments the IDC number after generating the JEP
    const newIdcs = [...idcs, [idcType, idcJep]];
    return [idc, idcJep, newIdcs];
}
exports.nextIDC = nextIDC;
/**
 * Add extra JFD fields and sort them by index in ascending order, removing duplicate indexes in the process
 * @param fields The fields from the JFD of a certain Type
 * @param extraFields The extra fields to add to the JFD
 * @returns A new record with the augmented fields
 */
function augmentJFDFields(fields, extraFields) {
    // Augment the type fields using the extra JFD fields definition
    return Object.keys(fields)
        .map((fieldType) => {
        // Skip non-existing default field definitions
        if (!(fieldType in types_1.DEFAULT_JFD_FIELDS))
            return { [fieldType]: fields[fieldType] };
        // Augment fields, put extra fields at the beginning so they override existing or incorrect values
        const augmentedFields = [...extraFields[fieldType], ...fields[fieldType]];
        // Temporary set to skip repeated indexes (keep only the first found)
        const found = new Set();
        // Sort them by index ascending and skip duplicate indexes
        const sortedFields = augmentedFields
            .sort(([, aIndex], [, bIndex]) => {
            const [, aIndexNum] = getIndexParts(aIndex);
            const [, bIndexNum] = getIndexParts(bIndex);
            return parseInt(aIndexNum) - parseInt(bIndexNum);
        })
            .filter(([, index]) => {
            if (found.has(index))
                return false;
            found.add(index);
            return true;
        });
        return { [fieldType]: sortedFields };
    })
        .reduce((typeFields, augmented) => {
        return Object.assign(Object.assign({}, typeFields), augmented);
    }, {});
}
exports.augmentJFDFields = augmentJFDFields;
/**
 * Retrieve a JEP from the JFD and translate some of the field's values to be fit for an EBTS file
 * @param jfd A valid JFD with data
 * @param fieldsByType The structure that contains the plain fields by type structure, needed for some of the checks
 */
function getJEP(jfd, fieldsByType) {
    jfd.onGenerateEBTSFile();
    return jfd.getFieldAttr((field) => {
        var _a, _b;
        const value = field.value;
        // Fix dates format
        if (value && field.type === 'date') {
            const date = new Date(value);
            if (isNaN(date.getTime())) {
                throw new Error(`Invalid date value: ${value}`);
            }
            else {
                return getEBTSDateStringFromDate(date, ((_a = field.EBTS) === null || _a === void 0 ? void 0 : _a.format) || 'YYYYMMDD');
            }
        }
        // Specific field cases
        if (field.name === 'NativeScanningResolution' || field.name === 'NominalTransmittingResolution') {
            if ((_b = fieldsByType[types_1.EbtsRecordType.Type4]) === null || _b === void 0 ? void 0 : _b.length)
                return '19.69';
            else
                return '00.00';
        }
        // Do not skip empty values for MONS
        if (field.getType() !== 'MONS') {
            if (value === '')
                return undefined;
        }
        // Skip fields that don't have an EBTS section
        // TODO: No need to validate this because fields without an EBTS key are already being skipped in
        //  sortJFDFieldsByType
        if (field.EBTS === undefined)
            return undefined;
        return value;
    });
}
exports.getJEP = getJEP;
/**
 * Augment a JEP with extra and mandatory fields. Extra fields won't override data in the JEP while mandatory do
 * @param jep
 * @param extra
 * @param mandatory
 */
function augmentJEP(jep, extra, mandatory) {
    // Only override those fields that don't have a value in the JEP
    const toOverride = Object.entries(extra !== null && extra !== void 0 ? extra : {}).filter(([key]) => isValueEmpty(jep[key]));
    const augmented = Object.assign(Object.assign({}, jep), Object.fromEntries(toOverride));
    // Always override values with defaults
    return Object.assign(Object.assign({}, augmented), (mandatory || {}));
}
exports.augmentJEP = augmentJEP;
/**
 * Override image metadata of all the images in the typed record
 * @param sortedImages
 * @param sourceAgency
 */
function overrideImageTypeRecordMetadata(sortedImages, sourceAgency) {
    return __awaiter(this, void 0, void 0, function* () {
        return yield Object.keys(sortedImages)
            .map((key) => {
            const fieldType = key;
            const images = sortedImages[fieldType];
            const overridden = images.map((image) => {
                return images_1.imageOverrideMetadata(image, fieldType, sourceAgency);
            });
            return [fieldType, overridden];
        })
            .reduce((p, [fieldType, overridden]) => __awaiter(this, void 0, void 0, function* () {
            // We need to await previous result
            const typeRecord = yield p;
            const values = yield Promise.all(overridden);
            return Object.assign(Object.assign({}, typeRecord), { [fieldType]: values });
        }), Promise.resolve({}));
    });
}
exports.overrideImageTypeRecordMetadata = overrideImageTypeRecordMetadata;
/**
 * Take in a JFD and a TOT and marshall the fields in an EBTS-ready format.
 * @param jfd A valid JFD with data
 * @param images An list of raw images
 * @returns A string with the marshalled fields
 */
function generateEBTSFileBuffer(jfd, images) {
    return __awaiter(this, void 0, void 0, function* () {
        // Get fields classified by type
        const fieldsByType = augmentJFDFields(sortJFDFieldsByType(jfd), types_1.DEFAULT_JFD_FIELDS);
        // Mapping of the field type and the records
        const records = new types_1.TypeRecord();
        // Save the generated IDCs to build the CNT later
        let idcs = [];
        let lastIdc = 0; // A dumb accumulator for the IDC values
        // Get a JEP from the JFD
        const jep = getJEP(jfd, fieldsByType);
        // If VersionNumber is non-blank, it was set by the JFD
        if (jfd.hasFieldDef('VersionNumber')) {
            const version = jfd.getValue('VersionNumber');
            if (version !== '')
                types_1.DEFAULT_T1_JEP.VersionNumber = version;
        }
        // Get the images from the JFD
        const zippedImages = images_1.zipImages(jfd.images, images || []);
        const sortedImages = images_1.sortImagesByType(zippedImages, jfd);
        // Override data in images
        const sourceAgency = jfd.getValue('OriginatingAgency');
        const overriddenImages = yield overrideImageTypeRecordMetadata(sortedImages, sourceAgency);
        // Skip generated fields
        const cleanedJEP = skipGeneratedFields(jep);
        // Construct records for each field type
        Object.keys(fieldsByType)
            // Skip Type-1 fields
            .filter((fieldType) => fieldType !== types_1.EbtsRecordType.Type1)
            .forEach((key) => {
            const fieldType = key;
            const typeFields = fieldsByType[fieldType];
            // Skip empty records
            if (typeFields.length === 0)
                return;
            // Extra stuff
            switch (fieldType) {
                case types_1.EbtsRecordType.Type2:
                    // Create an IDC and add to the JEP
                    const [nextIdc, idcJep, newIdcs] = nextIDC(idcs, fieldType, lastIdc);
                    const augmentedJep = augmentJEP(cleanedJEP, Object.assign({}, idcJep));
                    // Save new list of IDCs
                    idcs = newIdcs;
                    lastIdc = nextIdc;
                    // Map data from the JEP using field names and save
                    records[fieldType] = [getMappedRecord(typeFields, augmentedJep)];
                    break;
                // Images
                case types_1.EbtsRecordType.Type4:
                case types_1.EbtsRecordType.Type10:
                case types_1.EbtsRecordType.Type14:
                case types_1.EbtsRecordType.Type15:
                case types_1.EbtsRecordType.Type17:
                    const imageRecords = overriddenImages[fieldType] || [];
                    // Create an IDC and add to each image record
                    const augmentedImages = imageRecords.map((imageRecord) => {
                        // Generate an IDC
                        const [nextIdc, idcJep, newIdcs] = nextIDC(idcs, fieldType, lastIdc);
                        // Set the IDC and return the new image
                        const imageWithIdc = images_1.setImageIdc(imageRecord, lastIdc);
                        // Save new list of IDCs
                        idcs = newIdcs;
                        lastIdc = nextIdc;
                        // Map non-binary records' fields to the images' values and return the structure
                        if (fieldType === types_1.EbtsRecordType.Type10 ||
                            fieldType === types_1.EbtsRecordType.Type14 ||
                            fieldType === types_1.EbtsRecordType.Type15 ||
                            fieldType === types_1.EbtsRecordType.Type17) {
                            // Expand the info metadata with the actual image data so we map it too
                            // Construct the IDC key
                            // TODO: Use a mapping or something more "correct"
                            const idcKey = `ImageDesignationCharacterT${fieldType}`;
                            // Construct the image data key
                            let imageDataKey;
                            if (fieldType === types_1.EbtsRecordType.Type10) {
                                // Type-10 uses a different key
                                imageDataKey = 'Imagedata';
                            }
                            else if (fieldType === types_1.EbtsRecordType.Type17) {
                                // Type-17 uses a different key
                                imageDataKey = 'IrisImage';
                            }
                            else {
                                imageDataKey = `ImageDataT${fieldType}`;
                            }
                            // Also overwrite the IDC
                            const augmentedImageJep = Object.assign(Object.assign({}, imageRecord.info.metadata), { [idcKey]: idcJep.ImageDesignationCharacter, [imageDataKey]: imageRecord.data.data });
                            return getMappedRecord(typeFields, augmentedImageJep);
                        }
                        // Or return the image record as-is
                        else
                            return imageWithIdc;
                    });
                    // Map data from the JEP using mnemonics and save
                    records[fieldType] = [...augmentedImages];
                    break;
            }
        });
        // Get the TOT from the JFD
        const tot = jfd.getValue('TypeOfTransaction');
        if (!tot)
            throw new errors_1.MissingMandatoryFieldError('TypeOfTransaction');
        // Construct the Type-1 JEP
        const cntJep = getCNT(idcs, lastIdc);
        const totJep = getTOT(tot);
        const augmentedJep = augmentJEP(cleanedJEP, {}, Object.assign(Object.assign(Object.assign({}, cntJep), totJep), types_1.DEFAULT_T1_JEP));
        // Construct the Type-1 fields
        records[types_1.EbtsRecordType.Type1] = [getMappedRecord(fieldsByType[types_1.EbtsRecordType.Type1], augmentedJep)];
        // Marshall ASCII records
        return marshallRecords(records);
    });
}
exports.generateEBTSFileBuffer = generateEBTSFileBuffer;
/**
 * Take in a date string in some format combination of YYYY, MM and DD and return YYYY-MM-DD
 * @param date
 */
function getISODateStringFromEBTSDateString(date, format) {
    if (date.length != 8) {
        throw new Error(`Invalid date: ${date}`);
    }
    const yearIndex = format.indexOf('YYYY');
    const monthIndex = format.indexOf('MM');
    const dayIndex = format.indexOf('DD');
    if (yearIndex == -1 || monthIndex == -1 || dayIndex == -1) {
        throw new Error(`Invalid date format: ${format}`);
    }
    const yearPart = date.slice(yearIndex, yearIndex + 4);
    const monthPart = date.slice(monthIndex, monthIndex + 2);
    const datePart = date.slice(dayIndex, dayIndex + 2);
    return [yearPart, monthPart, datePart].join('-');
}
exports.getISODateStringFromEBTSDateString = getISODateStringFromEBTSDateString;
/**
 * Format a Date based on string format containing separate DD, MM and YYYY tags.
 * We perform basic validation on format.
 */
function getEBTSDateStringFromDate(date, format) {
    const yearIndex = format.indexOf('YYYY');
    const monthIndex = format.indexOf('MM');
    const dayIndex = format.indexOf('DD');
    if (yearIndex == -1 || monthIndex == -1 || dayIndex == -1) {
        throw new Error(`Invalid date format: ${format}`);
    }
    return format
        .replace('DD', `${date.getUTCDate()}`.padStart(2, '0'))
        .replace('MM', `${date.getUTCMonth() + 1}`.padStart(2, '0'))
        .replace('YYYY', `${date.getUTCFullYear()}`);
}
exports.getEBTSDateStringFromDate = getEBTSDateStringFromDate;
//# sourceMappingURL=ebts.js.map